<template>
  <v-dialog
    :value="value"
    content-class="crud-dialog"
    persistent
    max-width="480"
    @input="$emit('input', $event)"
  >
    <v-card class="crud-card">
      <v-card-title class="crud-card-title">
        <v-row no-gutters>
          <v-col cols="11" class="text-left">
            <h2 class="text-h5">{{ headerTitle }}</h2>
          </v-col>
          <v-col cols="1" class="text-right">
            <v-icon class="close" color="black" @click.stop="close">{{
              icons.mdiClose
            }}</v-icon>
          </v-col>
        </v-row>
      </v-card-title>
      <v-divider />
      <v-card-text class="crud-card-text">
        <p class="dialog-description">{{ headerSubtitle }}</p>
        <TextField
          v-if="requiresTitle"
          v-model="name"
          label="Title"
          name="name"
          placeholder="Add a document title"
          hint="* Mandatory"
          :required="requiresTitle"
          :errors="['* Mandatory']"
        />
        <v-textarea
          v-model="notes"
          label="Notes"
          outlined
          rows="6"
          no-resize
          hide-details="auto"
          class="notes-textarea"
        />
        <v-progress-linear
          v-if="busy"
          indeterminate
          color="primary"
          height="4"
          rounded
          class="mt-3"
        />
      </v-card-text>
      <v-card-actions class="crud-card-actions">
        <v-spacer />
        <GradientButton
          title="Cancel"
          callout=""
          :small="true"
          button-size="medium"
          :fill="false"
          :disabled="busy"
          @gradientButtonClicked="close"
        />
        <GradientButton
          class="ml-2"
          title="Save"
          callout=""
          :small="true"
          button-size="medium"
          data-test="save-edit"
          :disabled="busy || !canSubmit"
          :loading="busy"
          @gradientButtonClicked="submit"
        />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  import { mdiClose } from '@mdi/js';
  import { UPDATE_TESTATOR_DOCUMENT_MUTATION } from '../../constants/graphql/testator-documents';
  import TextField from '../common/components/TextField.vue';
  import GradientButton from '../guest/GradientButton.vue';

  export default {
    name: 'EditDocumentDialog',
    components: { TextField, GradientButton },
    props: {
      value: { type: Boolean, default: false },
      document: { type: Object, required: true },
      requiresTitle: { type: Boolean, default: false },
    },
    data() {
      return {
        icons: { mdiClose },
        name: this.document?.name || '',
        notes: this.document?.notes || '',
        busy: false,
      };
    },
    computed: {
      canSubmit() {
        if (this.requiresTitle && !(this.name || '').trim()) return false;
        return true;
      },
      headerTitle() {
        return this.requiresTitle ? 'Edit title or note' : 'Add note';
      },
      headerSubtitle() {
        return this.requiresTitle
          ? 'Update the title or add a note to help identify this document.'
          : 'Add a note to help your executor or family identify this document.';
      },
    },
    watch: {
      document: {
        immediate: true,
        handler(d) {
          this.name = d?.name || '';
          this.notes = d?.notes || '';
        },
      },
    },
    methods: {
      close() {
        if (this.busy) return;
        this.$emit('input', false);
      },
      async submit() {
        if (this.busy || !this.canSubmit) return;
        this.busy = true;
        try {
          const response = await this.$apollo.mutate({
            mutation: UPDATE_TESTATOR_DOCUMENT_MUTATION,
            variables: {
              id: this.document.id,
              name: this.name,
              notes: this.notes,
            },
          });
          this.$emit('saved', response.data.updateTestatorDocument);
          this.busy = false;
          this.$emit('input', false);
        } catch (e) {
          this.busy = false;
          this.$emit('error', e);
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import '../../constants/styles/modal.scss';

  .dialog-description {
    font-size: 14px;
    line-height: 20px;
    color: #6b7280;
    margin-bottom: 16px;
  }
  .close {
    cursor: pointer;
    transition: color 0.15s;
  }
  .close:hover {
    color: var(--omk-colour-icon-active-hover, #20491d) !important;
  }
</style>
